import { dayNumber, shiftHours, warningsFor, type Absence, type PlannedShift, type Person, type RuleSettings } from "./rules";

// The auto-draft: fills open slots with people, as a first draft for a
// manager to review. Greedy, hardest slots first (fewest people who could
// take them, then the longest). It never creates a rule break: someone who'd overlap, be on
// time off, lack the role, or break a labour rule isn't used — the slot
// stays a visible gap instead. Among the rest it prefers people below their
// target hours and on days they asked for. Pure and deterministic.
// See docs/plans/workday/staff_planning.md (phase 3).

export type Preference = {
  staff_id: number;
  /** Desired hours for the whole range (already prorated by the caller). */
  target_hours: number | null;
  weekdays: number[];
  dates: string[];
};

export type DraftInput = {
  /** Every shift in and around the range (assigned ones count for rules). */
  shifts: PlannedShift[];
  /** The open slots to fill (staff_id null), by id. */
  slotIds: number[];
  people: Person[];
  preferences: Preference[];
  absences: Absence[];
  settings: RuleSettings;
};

export type DraftResult = {
  assignments: { shift_id: number; staff_id: number }[];
  unfilled: { shift_id: number; reason: "no_one_with_role" | "no_one_free" | "rules" }[];
  hours: Map<number, number>;
};

const isoDow = (iso: string) => ((dayNumber(iso) + 3) % 7 + 7) % 7 + 1;

export function autoDraft(input: DraftInput): DraftResult {
  const plan = input.shifts.map((s) => ({ ...s }));
  const byId = new Map(plan.map((s) => [s.id, s]));
  const prefs = new Map(input.preferences.map((p) => [p.staff_id, p]));
  const people = [...input.people].sort((a, b) => a.id - b.id);

  const slots = input.slotIds.map((id) => byId.get(id)).filter((s): s is PlannedShift => !!s && s.staff_id == null);
  const hours = new Map<number, number>();
  for (const s of plan) if (s.staff_id != null) hours.set(s.staff_id, (hours.get(s.staff_id) ?? 0) + shiftHours(s));

  const eligible = (slot: PlannedShift) => people.filter((p) => slot.role_id == null || p.role_ids.includes(slot.role_id));

  // Hardest first: fewest people with the role; then longer shifts (more of
  // the service covered when not everything can be); then earliest.
  const order = [...slots].sort(
    (a, b) =>
      eligible(a).length - eligible(b).length ||
      shiftHours(b) - shiftHours(a) ||
      a.shift_date.localeCompare(b.shift_date) ||
      a.start_time.localeCompare(b.start_time) ||
      a.id - b.id,
  );

  const assignments: DraftResult["assignments"] = [];
  const unfilled: DraftResult["unfilled"] = [];

  for (const slot of order) {
    const withRole = eligible(slot);
    if (!withRole.length) {
      unfilled.push({ shift_id: slot.id, reason: "no_one_with_role" });
      continue;
    }
    let free = 0;
    let best: { person: Person; score: number } | null = null;
    for (const person of withRole) {
      const warnings = warningsFor(slot, person, plan, input.settings, input.absences);
      if (warnings.some((w) => w.kind === "overlap" || w.kind === "time_off")) continue;
      free++;
      if (warnings.length) continue; // would break a labour rule
      const score = scoreFor(slot, person, hours.get(person.id) ?? 0, prefs.get(person.id));
      if (!best || score < best.score) best = { person, score };
    }
    if (!best) {
      unfilled.push({ shift_id: slot.id, reason: free ? "rules" : "no_one_free" });
      continue;
    }
    slot.staff_id = best.person.id;
    hours.set(best.person.id, (hours.get(best.person.id) ?? 0) + shiftHours(slot));
    assignments.push({ shift_id: slot.id, staff_id: best.person.id });
  }

  return { assignments, unfilled, hours };
}

/** Lower is better: under target hours, on a day they asked for. */
function scoreFor(slot: PlannedShift, person: Person, planned: number, pref: Preference | undefined) {
  const target = pref?.target_hours ?? null;
  const after = planned + shiftHours(slot);
  // Share of their target already used; people without a target sit in the middle.
  let score = target && target > 0 ? (after / target) * 100 : 60 + after;
  if (target && after > target) score += 50;
  if (pref && (pref.weekdays.length || pref.dates.length)) {
    const wanted = pref.dates.includes(slot.shift_date) || pref.weekdays.includes(isoDow(slot.shift_date));
    score += wanted ? -25 : 25;
  }
  return score;
}
